import { useState, useMemo } from "react";
import { OptionType } from "@core/types/select.types";
import { orgInvitationEventBus } from "@platform/events/orgInvitation.events";
import { useFetchOrganizations } from "@core/features/organization";

type UseOrgLookupOptions = {
  onClose?: () => void;
  /** Whether to fetch organizations. Pass false to defer loading until needed. */
  shouldLoad?: boolean;
};

export function useOrgLookup({
  onClose,
  shouldLoad = true,
}: UseOrgLookupOptions = {}) {
  const { options: loadedOptions, isLoading } = useFetchOrganizations(shouldLoad);

  // Track user-created options locally
  const [createdOptions, setCreatedOptions] = useState<OptionType[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OptionType | null>(null);

  // Combine loaded and created options
  const options = useMemo(
    () =>
      [...loadedOptions, ...createdOptions].filter(
        (option, index, self) =>
          index === self.findIndex((o) => o.value === option.value)
      ),
    [loadedOptions, createdOptions]
  );

  const handleSelectOrganization = (newOption: OptionType | null) => {
    if (!newOption) return;

    setSelectedOptions((prev) => {
      const exists = prev.some((opt) => opt.value === newOption.value);
      return exists ? prev : [...prev, newOption];
    });

    setSelectedOrg(null);
  };

  const handleCreateOrganization = (inputValue: string) => {
    const newOption: OptionType = {
      label: inputValue,
      value: `new-${inputValue.toLowerCase().replace(/\s+/g, "-")}`,
      data: { created: true },
    };

    setCreatedOptions((prev) => {
      const exists = prev.some((opt) => opt.label === newOption.label);
      return exists ? prev : [...prev, newOption];
    });

    setSelectedOptions((prev) => [...prev, newOption]);

    orgInvitationEventBus.emitEvent("lookupOrg:notFound", { name: inputValue });

    setSelectedOrg(null);
  };

  const handleClose = () => {
    setSelectedOptions([]);
    setSelectedOrg(null);
    onClose?.();
  };

  return {
    handleClose,
    handleSelectOrganization,
    handleCreateOrganization,
    options,
    isLoading,
    selectedOptions,
    selectedOrg,
  };
}

export default useOrgLookup;

